import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import cn from 'classnames';
import './BrandOnboardingShopifyConnectionPanel.scss';

import { updateBrandDomainToSetup } from '../../../Actions/OnboardingActions';
import { saveShopifyIntegration } from '../../../Actions/BrandActions';
import { brandOnboardingGetDomainToSetup } from '../../../Helpers/onboarding_helpers';

import BrandOnboardingProgressBar from '../BrandOnboardingProgressBar';
import Tooltip from '../../General/Tooltip';
import BrandOnboardingShopifyFAQ from '../BrandOnboardingShopifyFAQ';

const BrandOnboardingShopifyConnectionPanel = props => {
  const { onboarding } = props;
  const domainToSetup = brandOnboardingGetDomainToSetup(onboarding);

  useEffect(() => {
    if (!domainToSetup) return;

    if (!domainToSetup.setupInProgress) props.updateBrandDomainToSetup(domainToSetup.domain, { setupInProgress: true });
  }, [domainToSetup]);

  const updateField = (prev, idx, patch) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f));
  const [fields, setFields] = useState([
    {
      name: 'domain',
      label: 'Shopify Store Domain',
      value: domainToSetup?.domain || '',
      isFieldValid: () => true,
      isValid: true,
      errorMessage: 'Please enter your Shopify store name',
      placeholder: 'domain.com',
      supportText: 'https://',
      inputValueAlignRight: true,
      disabled: true
    },
    {
      name: 'shopName',
      label: 'Shopify Store Name',
      value: domainToSetup?.shopName || '',
      isFieldValid: value => value?.length > 1,
      isValid: true,
      errorMessage: 'Please enter your Shopify store name',
      placeholder: 'shop-my-app',
      supportText: '.myshopify.com',
      inputValueAlignRight: false,
      disabled: false
    },
    {
      name: 'privateKey',
      label: 'API secret key',
      value: domainToSetup?.privateKey || '',
      isFieldValid: value => value?.length > 1,
      isValid: true,
      errorMessage: "Please enter your Shopify app's API secret key",
      placeholder: 'db18c89f48196f20675fb23a075d029d',
      supportText: '',
      inputValueAlignRight: false,
      disabled: false
    },
    {
      name: 'adminAccessToken',
      label: 'Admin API access token',
      value: domainToSetup?.adminAccessToken || '',
      isFieldValid: value => value?.length > 1,
      isValid: true,
      errorMessage: "Please enter your Shopify app's Admin API access token",
      placeholder: 'shpat_b7d27f85a3f4170139aa1ba4f307d04',
      supportText: '',
      inputValueAlignRight: false,
      disabled: false
    }
  ]);

  const onNextClick = async skipSettingUpShopifyForDomain => {
    if (props.isLoading) return;

    if (skipSettingUpShopifyForDomain) {
      props.updateBrandDomainToSetup(domainToSetup.domain, { skipShopifySetup: true });
      return props.goToPanel('Network Configuration', 'Domain Setup');
    }

    const [domain, shopName, privateKey, adminAccessToken] = fields.map(f => f.value.trim());
    if (!domain?.length) return window.ALERT.error('Please enter the Shopify store domain. (What customers see in the URL)');
    else if (!shopName?.length) return window.ALERT.error("Please enter your Shopify's store name. (storeName.myshopify.com)");
    else if (!privateKey?.length) return window.ALERT.error("Please enter your Shopify app's API secret key.");
    else if (!adminAccessToken?.length) return window.ALERT.error("Please enter your Shopify app's Admin API access token.");

    props.setIsLoading(true);
    const shopifyIntegrationResponse = await props.saveShopifyIntegration(
      {
        domain,
        shopName,
        privateKey,
        adminAccessToken
      },
      {
        allowAffiliateTracking: false // NOTE: hardcoding for phase 1 & 2 release
      }
    );

    if (shopifyIntegrationResponse.error) {
      props.setIsLoading(false);
      window.ALERT.error(shopifyIntegrationResponse.error || 'An error occurred while connecting your Shopify store. Please try again.');
    } else {
      props.updateBrandDomainToSetup(domain, { connectedShopify: true, shopName, privateKey, adminAccessToken });
      props.goToNextPanel();
    }
  };

  return (
    <div className='brand-onboarding-shopify-connection-panel-outer'>
      <div className='brand-onboarding-shopify-connection-panel-inner'>
        <div className='brand-onboarding-split-container-outer'>
          <div className='brand-onboarding-split-container-inner'>
            <div className='brand-onboarding-graphic-container-outer'>
              <div className='brand-onboarding-graphic-container-inner content-start'>
                <BrandOnboardingShopifyFAQ />

                <BrandOnboardingProgressBar
                  goToPanel={props.goToPanel}
                  sections={props.sections}
                  activeSectionName={props.activeSectionName}
                  activePanelName={props.activePanelName}
                />
              </div>

              <div className='brand-onboarding-graphic-background blur'>
                <img className='brand-onboarding-graphic-background-image' src={props.backgroundImage} alt='name panel graphic' />
                <div className='brand-onboarding-graphic-background-overlay'></div>
              </div>
            </div>
            <div className='brand-onboarding-input-container-outer'>
              <div className='brand-onboarding-input-container-number-marker'>{props.panelNumber}</div>

              <div className={cn('brand-onboarding-input-container-inner', { isLoading: props.isLoading })}>
                <div className='brand-onboarding-title-text'>Setup Shopify App</div>
                <div className='brand-onboarding-subtitle-text'>Integrate your Shopify store with our app.</div>

                <ShopifyConnectionExplanation />

                <div className='brand-onboarding-input-label'>Enter Your App's Credentials</div>
                {fields.map((field, index) => (
                  <div key={index} className='brand-onboarding-input-container'>
                    <Tooltip message={field.isValid ? '' : field.errorMessage}>
                      {field.customComponent ? (
                        field.customComponent(field, index)
                      ) : (
                        <input
                          className={cn('brand-onboarding-input', {
                            invalid: !field.isValid,
                            alignRight: field.inputValueAlignRight,
                            filled: !!field.value
                          })}
                          type='text'
                          placeholder={field.label}
                          id={field.name}
                          value={field.value}
                          disabled={field.disabled}
                          onChange={e => {
                            const { value } = e.target;
                            setFields(prev => updateField(prev, index, { value: value?.trim() }));
                          }}
                        />
                      )}
                    </Tooltip>

                    {!!field.supportText && (
                      <div className={cn('brand-onboarding-input-support-text', { alignRight: !field.inputValueAlignRight })}>
                        {field.supportText}
                      </div>
                    )}
                  </div>
                ))}

                <div className='brand-onboarding-pagination-buttons'>
                  <button className='brand-onboarding-button stretch' onClick={() => props.goToPreviousPanel()}>
                    Back
                  </button>
                  <button className='brand-onboarding-button stretch primary' onClick={() => onNextClick()}>
                    Connect Shopify
                  </button>
                </div>
                <Tooltip message='You can connect shopify later in your account settings'>
                  <button className='brand-onboarding-skip-panel-button' onClick={() => onNextClick(true)}>
                    Skip for now
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * This is a breakdown on how exactly to create ShopMy's custom Shopify app and
 * connect it to our platform. Please keep this up to date with:
 * https://docs.shopmy.us/update/reference/create-shopmys-shopify-app#/
 */
export const ShopifyConnectionExplanation = () => {
  return (
    <div className='shopify-connection-explanation-outer'>
      <div className='shopify-connection-explanation-inner'>
        <video
          className='shopify-connection-explanation-video'
          controls
          src='https://askemma-static-public.s3.us-east-2.amazonaws.com/explainers/shopify/shopify_integration_setup.mov'
        />

        <div className='shopify-connection-explanation-general-instruction'>
          Log in to your Shopify Admin store and complete the following steps to create a full Shopify integration with ShopMy.
        </div>

        <div className='shopify-connection-explanation-step-container'>
          <details className='shopify-connection-explanation-step'>
            <summary className='shopify-connection-explanation-step-title'>Navigate to Apps Panel</summary>
            <ol className='shopify-connection-explanation-step-list'>
              <li className='shopify-connection-explanation-step-item'>Click the "Apps" button on the left side panel</li>
              <li className='shopify-connection-explanation-step-item'>Click the "App and sales channel settings" option in the dropdown</li>
            </ol>
            <img
              className='shopify-connection-explanation-step-image'
              src='https://askemma-static-public.s3.us-east-2.amazonaws.com/explainers/shopify/shopify_dashboard_screenshot.png'
            />
          </details>

          <details className='shopify-connection-explanation-step'>
            <summary className='shopify-connection-explanation-step-title'>Develop New App</summary>
            <ol className='shopify-connection-explanation-step-list'>
              <li className='shopify-connection-explanation-step-item'>Click the "Develop Apps" button in the top right corner</li>
              <li className='shopify-connection-explanation-step-item'>Create a new app</li>
              <li className='shopify-connection-explanation-step-item'>Grant access for custom apps to be developed</li>
              <li className='shopify-connection-explanation-step-item'>Enter "Shopmy Integration" for your app name and click "Create App"</li>
            </ol>
            <img
              className='shopify-connection-explanation-step-image'
              src='https://askemma-static-public.s3.us-east-2.amazonaws.com/explainers/shopify/develop_new_image_panel.png'
            />
          </details>

          <details className='shopify-connection-explanation-step'>
            <summary className='shopify-connection-explanation-step-title'>Configure App's Scopes</summary>
            <ol className='shopify-connection-explanation-step-list'>
              <li className='shopify-connection-explanation-step-item'>Click "Configure Admin API scopes" under the "Overview" tab</li>
              <li className='shopify-connection-explanation-step-item'>
                Allow the following required scopes (8 total). Please note, if all of these permissions are not granted, your onboarding process will
                be halted:
                <ol className='shopify-connection-explanation-step-item-list'>
                  <li className='shopify-connection-explanation-step-sub-item'>
                    <code>write_discounts :: read_discounts</code>
                  </li>
                  <li className='shopify-connection-explanation-step-sub-item'>
                    <code>write_price_rules :: read_price_rules</code>
                  </li>
                  <li className='shopify-connection-explanation-step-sub-item'>
                    <code>write_orders :: read_orders</code>
                  </li>
                  <li className='shopify-connection-explanation-step-sub-item'>
                    <code>read_products</code>
                  </li>
                  <li className='shopify-connection-explanation-step-sub-item'>
                    <code>read_inventory</code>
                  </li>
                </ol>
              </li>
              <li className='shopify-connection-explanation-step-item'>
                Allow optional scopes (if needed):
                <ol className='shopify-connection-explanation-step-item-list'>
                  <li className='shopify-connection-explanation-step-sub-item'>
                    <code>read_customers</code> - Add this scope if you are looking to do new vs returning customer commission rates. Please note we
                    do not recommend this setting new vs returning rates as a strategy.
                  </li>
                  <li className='shopify-connection-explanation-step-sub-item'>
                    <code>write_draft_orders :: read_draft_orders :: write_customers</code>- Add these scopes to enable us to create and complete
                    Shopify draft orders for gifting to be compatible with Shopify Flow workflows you may have in place.
                  </li>
                  <li className='shopify-connection-explanation-step-sub-item'>
                    <code>read_locations</code>- Add this scope if you'd like to specify specific warehouses or store locations from which we should
                    sync your product inventory levels.
                  </li>
                </ol>
              </li>
              <li className='shopify-connection-explanation-step-item'>Save the changes</li>
            </ol>
            <img
              className='shopify-connection-explanation-step-image'
              src='https://askemma-static-public.s3.us-east-2.amazonaws.com/explainers/shopify/configure_scopes_screenshot.png'
            />
          </details>

          <details className='shopify-connection-explanation-step'>
            <summary className='shopify-connection-explanation-step-title'>Get Your Tokens</summary>
            <ol className='shopify-connection-explanation-step-list'>
              <li className='shopify-connection-explanation-step-item'>Click the "API credentials" tab header</li>
              <li className='shopify-connection-explanation-step-item'>
                Locate your shop name. This will be in the form of (shopname).myshopify.com. Only the shopname is needed.
              </li>
              <li className='shopify-connection-explanation-step-item'>Locate the API secret key.</li>
              <li className='shopify-connection-explanation-step-item'>Click "Install App", then "Reveal token once"</li>
              <li className='shopify-connection-explanation-step-item'>Locate the new Admin API access token that you just revealed</li>
              <li className='shopify-connection-explanation-step-item'>Paste those values into the inputs below</li>
            </ol>
            <img
              className='shopify-connection-explanation-step-image'
              src='https://askemma-static-public.s3.us-east-2.amazonaws.com/explainers/shopify/tokens_screenshot.png'
            />
          </details>
        </div>
      </div>
    </div>
  );
};

BrandOnboardingShopifyConnectionPanel.propTypes = {
  onboarding: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  updateBrandDomainToSetup: PropTypes.func.isRequired,
  saveShopifyIntegration: PropTypes.func.isRequired,

  goToPreviousPanel: PropTypes.func.isRequired,
  goToNextPanel: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
  onboarding: state.onboarding,
  user: state.user
});

export default connect(mapStateToProps, {
  updateBrandDomainToSetup,
  saveShopifyIntegration
})(withRouter(BrandOnboardingShopifyConnectionPanel));
